import { modalAnatomy } from '@chakra-ui/anatomy';
import {
  SystemStyleObject,
  createMultiStyleConfigHelpers,
  defineStyle,
} from '@chakra-ui/react';
import {
  getBorderWidthToken,
  getBoxSizingStyles,
  getTypographyToken,
  runIfFn,
  pxToRem,
} from '@allergan-data-labs/alle-elements-core';

const chakraModalCloseButtonSelector = '.chakra-modal__close-btn';

const { defineMultiStyleConfig, definePartsStyle } =
  createMultiStyleConfigHelpers(modalAnatomy.keys);

const baseStyleOverlay = defineStyle((props) => {
  const { colorScheme: c } = props;
  return {
    zIndex: 'modal',
    bg: `${c}.Container/Neutral/Inverted 40`,
  };
});

const baseStyleHeader = defineStyle({
  ...getTypographyToken('Headline/Extra small/Primary medium'),
  fontWeight: 'Medium',
  h: '32',
  pt: '24',
  px: '24',
  pb: '16',
  p: {
    m: '0',
  },
});

export const getCloseButtonStyles = ({
  hideCloseButton = false,
}: {
  hideCloseButton: boolean;
}): SystemStyleObject => {
  return {
    [chakraModalCloseButtonSelector]: {
      display: hideCloseButton ? 'none' : 'flex',
    },
  };
};

const baseStyleCloseButton = defineStyle((props) => {
  const { colorScheme: c } = props;
  return {
    ...getBoxSizingStyles(),
    cursor: 'pointer',
    position: 'unset',
    h: '32',
    w: '32',
    p: '6',
    borderRadius: 'Circle',
    color: `${c}.Icon/Neutral/Default`,
    _focusVisible: {
      outlineColor: `${c}.Border/Brand/Focus ring/Default`,
      outlineStyle: 'solid',
      outlineWidth: getBorderWidthToken('Bold'),
      outlineOffset: '-2px',
    },
  };
});

const baseStyleFooter = defineStyle((props) => {
  const { colorScheme: c } = props;
  return {
    p: '24',
    borderTop: '1px',
    borderColor: `${c}.Border/Neutral/Subtle 2`,
  };
});

const baseStyleDialogContainer = defineStyle((props) => {
  const { isCentered, scrollBehavior } = props;
  return {
    display: 'flex',
    zIndex: 'modal',
    justifyContent: 'center',
    alignItems: isCentered ? 'center' : 'flex-start',
    overflow: scrollBehavior === 'inside' ? 'hidden' : 'auto',
    overscrollBehaviorY: 'none',
  };
});

const baseStyleDialog = defineStyle((props) => {
  const { colorScheme: c, scrollBehavior, size } = props;
  return {
    bg: `${c}.Container/Neutral/Base`,
    color: `${c}.Text/Neutral/Default`,
    borderRadius: 'Medium',
    boxShadow: 'Elevation/4',
    w: '100%',
    minW: '288px',
    maxW: '676px',
    zIndex: 'modal',
    maxH: scrollBehavior === 'inside' ? 'calc(100% - 7.5rem)' : undefined,
    ...((size !== 'full' && { margin: '16' }) || {}),
  };
});

const baseStyleBody = defineStyle((props) => {
  const { scrollBehavior } = props;

  return {
    pt: '8',
    pb: '24',
    px: '24',
    flex: '1',
    overflow: scrollBehavior === 'inside' ? 'auto' : undefined,
  };
});

const baseStyle = definePartsStyle((props) => ({
  overlay: runIfFn(baseStyleOverlay, props),
  dialogContainer: runIfFn(baseStyleDialogContainer, props),
  dialog: runIfFn(baseStyleDialog, props),
  header: baseStyleHeader,
  closeButton: runIfFn(baseStyleCloseButton, props),
  body: runIfFn(baseStyleBody, props),
  footer: runIfFn(baseStyleFooter, props),
}));

const sizes = {
  xs: definePartsStyle({
    dialog: {
      // S Mobile | M Mobile | Tablet | L Desktop | XL Desktop
      maxW: ['90%', '90%', '46%', '31%', '21%'],
      maxH: ['90%', '90%', '42%', '42%', '42%'],
    },
  }),
  sm: definePartsStyle({
    dialog: {
      maxW: ['90%', '90%', '58%', '47%', '29%'],
      maxH: ['90%', '90%', '58%', '58%', '58%'],
    },
  }),
  md: definePartsStyle({
    dialog: {
      maxW: ['90%', '90%', '82%', '63%', '45%'],
      maxH: ['90%', '90%', '74%', '74%', '74%'],
    },
  }),
  lg: definePartsStyle({
    dialog: {
      maxW: ['90%', '90%', '94%', '95%', '69%'],
      maxH: ['90%', '90%', '90%', '90%', '90%'],
    },
  }),
  xl: definePartsStyle({
    dialog: {
      maxW: `calc(100vw - ${pxToRem(`24px`)})`,
      maxH: `calc(100vh - ${pxToRem('24px')})`,
      h: '100%',
      w: '100%',
    },
  }),
  full: definePartsStyle({
    dialog: {
      width: '100%',
      height: '100%',
    },
  }),
};

export const ModalTheme = defineMultiStyleConfig({
  baseStyle,
  sizes,
  defaultProps: {
    size: 'md',
  },
});
